export type ScanBufferStatus = 'empty' | 'pending' | 'ready';

export type ScanBufferReason =
  | 'empty'
  | 'force'
  | 'incomplete_url'
  | 'url_ready'
  | 'manual_ready'
  | 'manual_too_short';

export interface ScanInputBuffer {
  input: string;
  reason: ScanBufferReason;
  status: ScanBufferStatus;
}

interface BufferScanInputOptions {
  force?: boolean;
}

export function normalizeScanInput(input: unknown): string {
  return String(input || '')
    .trim()
    .replace(/\s+/g, '');
}

export function manualCodeCandidate(input: unknown): string {
  return normalizeScanInput(input)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

export function isUrlLikeInput(input: unknown): boolean {
  const normalizedInput = normalizeScanInput(input);
  return (
    /^https?:\/?/i.test(normalizedInput) ||
    normalizedInput.includes('/') ||
    normalizedInput.includes('.') ||
    /gophoto/i.test(normalizedInput)
  );
}

export function isLikelyIncompleteUrl(input: unknown): boolean {
  const normalizedInput = normalizeScanInput(input);

  if (/^https?:\/{0,2}[^/]*\/?$/i.test(normalizedInput)) {
    return true;
  }

  const domainPathMatch = normalizedInput.match(
    /^(?:https?:\/\/)?[^/\s]+\.[^/\s]+\/([^?#]*)$/i
  );
  if (!domainPathMatch) {
    return false;
  }

  const pathSegments = (domainPathMatch[1] ?? '').split('/').filter(Boolean);
  if (pathSegments.length === 0) {
    return true;
  }

  if (pathSegments.some((segment) => /^[A-Za-z0-9]{6}$/.test(segment))) {
    return false;
  }

  return (pathSegments.at(-1) || '').length < 6;
}

export function bufferScanInput(
  input: unknown,
  { force = false }: BufferScanInputOptions = {}
): ScanInputBuffer {
  const normalizedInput = normalizeScanInput(input);

  if (!normalizedInput) {
    return { status: 'empty', input: '', reason: 'empty' };
  }

  if (force) {
    return { status: 'ready', input: normalizedInput, reason: 'force' };
  }

  if (isUrlLikeInput(normalizedInput)) {
    return isLikelyIncompleteUrl(normalizedInput)
      ? {
          status: 'pending',
          input: normalizedInput,
          reason: 'incomplete_url',
        }
      : { status: 'ready', input: normalizedInput, reason: 'url_ready' };
  }

  return manualCodeCandidate(normalizedInput).length >= 6
    ? { status: 'ready', input: normalizedInput, reason: 'manual_ready' }
    : {
        status: 'pending',
        input: normalizedInput,
        reason: 'manual_too_short',
      };
}

export function displayedScanCode(code: string, rawInput: string): string {
  if (code) {
    return code;
  }

  const normalizedInput = normalizeScanInput(rawInput);
  return isUrlLikeInput(normalizedInput)
    ? ''
    : manualCodeCandidate(normalizedInput).slice(0, 6);
}
